import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Save, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';

interface Benefit {
  title: string;
  description: string;
}

interface SitePageData {
  id?: string;
  site_id: string;
  page_type: 'buyer' | 'seller';
  headline: string;
  subheadline: string;
  intro_text: string;
  benefits_json: Benefit[];
  cta_text: string;
  cta_button_text: string;
  seo_title: string;
  seo_description: string;
  og_title: string;
  og_description: string;
  is_published: boolean;
}

export default function SitePageEdit() {
  const { pageType } = useParams<{ pageType: 'buyer' | 'seller' }>();
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, selectedSite, isGlobalAdmin, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [pageData, setPageData] = useState<SitePageData | null>(null);

  const pageTitle = pageType === 'buyer' ? 'Buyer Page' : 'Seller Page';

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (selectedSiteId && pageType) {
      fetchPageData();
    }
  }, [selectedSiteId, pageType]);

  const fetchPageData = async () => {
    if (!selectedSiteId || !pageType) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('site_pages' as any)
        .select('*')
        .eq('site_id', selectedSiteId)
        .eq('page_type', pageType)
        .maybeSingle();

      if (error) throw error;

      const row = data as any;
      if (row) {
        setPageData({
          id: row.id,
          site_id: row.site_id,
          page_type: row.page_type as 'buyer' | 'seller',
          headline: row.headline || '',
          subheadline: row.subheadline || '',
          intro_text: row.intro_text || '',
          benefits_json: (Array.isArray(row.benefits_json) ? row.benefits_json : []) as Benefit[],
          cta_text: row.cta_text || '',
          cta_button_text: row.cta_button_text || '',
          seo_title: row.seo_title || '',
          seo_description: row.seo_description || '',
          og_title: row.og_title || '',
          og_description: row.og_description || '',
          is_published: row.is_published,
        });
      } else {
        // Initialize with empty defaults
        setPageData({
          site_id: selectedSiteId,
          page_type: pageType,
          headline: '',
          subheadline: '',
          intro_text: '',
          benefits_json: [],
          cta_text: '',
          cta_button_text: pageType === 'buyer' ? 'Start My Home Search' : 'Get My Home Value',
          seo_title: '',
          seo_description: '',
          og_title: '',
          og_description: '',
          is_published: true,
        });
      }
    } catch (error) {
      console.error('Error fetching page data:', error);
      toast.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pageData || !selectedSiteId) return;
    
    setSaving(true);
    try {
      const upsertData = {
        site_id: selectedSiteId,
        page_type: pageData.page_type,
        headline: pageData.headline,
        subheadline: pageData.subheadline,
        intro_text: pageData.intro_text,
        benefits_json: pageData.benefits_json as unknown as object[],
        cta_text: pageData.cta_text,
        cta_button_text: pageData.cta_button_text,
        seo_title: pageData.seo_title,
        seo_description: pageData.seo_description,
        og_title: pageData.og_title,
        og_description: pageData.og_description,
        is_published: pageData.is_published,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('site_pages' as any)
        .upsert(upsertData as any, {
          onConflict: 'site_id,page_type'
        });

      if (error) throw error;
      toast.success('Page saved successfully');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedSiteId || !pageType) return;
    
    setRegenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke('generate-site-page', {
        body: { siteId: selectedSiteId, pageType },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (response.error) throw response.error;
      
      toast.success('Page content regenerated successfully');
      await fetchPageData();
    } catch (error: any) {
      console.error('Error regenerating page:', error);
      toast.error(error.message || 'Failed to regenerate page');
    } finally {
      setRegenerating(false);
    }
  };

  const updateBenefit = (index: number, field: 'title' | 'description', value: string) => {
    if (!pageData) return;
    const newBenefits = [...pageData.benefits_json];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    setPageData({ ...pageData, benefits_json: newBenefits });
  };

  const addBenefit = () => {
    if (!pageData) return;
    setPageData({
      ...pageData,
      benefits_json: [...pageData.benefits_json, { title: '', description: '' }],
    });
  };

  const removeBenefit = (index: number) => {
    if (!pageData) return;
    const newBenefits = pageData.benefits_json.filter((_, i) => i !== index);
    setPageData({ ...pageData, benefits_json: newBenefits });
  };

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Page not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Edit {pageTitle} | Admin</title>
      </Helmet>

      <div className="container max-w-4xl py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-display font-bold">Edit {pageTitle}</h1>
              {selectedSite && (
                <p className="text-sm text-muted-foreground">{selectedSite.name}</p>
              )}
            </div>
          </div>
          {isGlobalAdmin && <SiteSwitcher />}
        </div>

        <div className="space-y-6">
          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={handleRegenerate} disabled={regenerating}>
              <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
              {regenerating ? 'Regenerating...' : 'Regenerate with AI'}
            </Button>
          </div>

          {/* Hero Content */}
          <Card>
            <CardHeader>
              <CardTitle>Hero Section</CardTitle>
              <CardDescription>The main headline and introduction shown at the top of the page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={pageData.headline}
                  onChange={(e) => setPageData({ ...pageData, headline: e.target.value })}
                  placeholder="Enter main headline..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subheadline">Subheadline</Label>
                <Textarea
                  id="subheadline"
                  value={pageData.subheadline}
                  onChange={(e) => setPageData({ ...pageData, subheadline: e.target.value })}
                  placeholder="Supporting text below headline..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="intro_text">Introduction Text</Label>
                <Textarea
                  id="intro_text"
                  value={pageData.intro_text}
                  onChange={(e) => setPageData({ ...pageData, intro_text: e.target.value })}
                  placeholder="Introduction paragraph..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Benefits */}
          <Card>
            <CardHeader>
              <CardTitle>Benefits / Services</CardTitle>
              <CardDescription>Key benefits displayed as cards on the page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {pageData.benefits_json.map((benefit, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Benefit {index + 1}</Label>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBenefit(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <Input
                    value={benefit.title}
                    onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                    placeholder="Benefit title..."
                  />
                  <Textarea
                    value={benefit.description}
                    onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                    placeholder="Benefit description..."
                    rows={2}
                  />
                </div>
              ))}
              <Button variant="outline" onClick={addBenefit}>
                <Plus className="h-4 w-4 mr-2" />
                Add Benefit
              </Button>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <Card>
            <CardHeader>
              <CardTitle>Call to Action</CardTitle>
              <CardDescription>The text encouraging visitors to fill out the form</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cta_text">CTA Text</Label>
                <Textarea
                  id="cta_text"
                  value={pageData.cta_text}
                  onChange={(e) => setPageData({ ...pageData, cta_text: e.target.value })}
                  placeholder="Text encouraging form submission..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cta_button_text">Button Text</Label>
                <Input
                  id="cta_button_text"
                  value={pageData.cta_button_text}
                  onChange={(e) => setPageData({ ...pageData, cta_button_text: e.target.value })}
                  placeholder="Submit button text..."
                />
              </div>
            </CardContent>
          </Card>

          {/* SEO */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Settings</CardTitle>
              <CardDescription>Meta tags for search engine optimization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="seo_title">SEO Title</Label>
                <Input
                  id="seo_title"
                  value={pageData.seo_title}
                  onChange={(e) => setPageData({ ...pageData, seo_title: e.target.value })}
                  placeholder="SEO meta title (50-60 characters)..."
                />
                <p className="text-xs text-muted-foreground">{pageData.seo_title?.length || 0}/60 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="seo_description">SEO Description</Label>
                <Textarea
                  id="seo_description"
                  value={pageData.seo_description}
                  onChange={(e) => setPageData({ ...pageData, seo_description: e.target.value })}
                  placeholder="SEO meta description (150-160 characters)..."
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">{pageData.seo_description?.length || 0}/160 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="og_title">Open Graph Title</Label>
                <Input
                  id="og_title"
                  value={pageData.og_title}
                  onChange={(e) => setPageData({ ...pageData, og_title: e.target.value })}
                  placeholder="Social share title..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="og_description">Open Graph Description</Label>
                <Textarea
                  id="og_description"
                  value={pageData.og_description}
                  onChange={(e) => setPageData({ ...pageData, og_description: e.target.value })}
                  placeholder="Social share description..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Publishing */}
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Switch
                  checked={pageData.is_published}
                  onCheckedChange={(checked) => setPageData({ ...pageData, is_published: checked })}
                />
                <Label>Page is published and visible to visitors</Label>
              </div>
            </CardContent>
          </Card>

          {/* Save Button at Bottom */}
          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
